import React, { useEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modal — one accessible dialog primitive for the whole app.
 *  - Renders into a portal on <body>, above everything.
 *  - Backdrop blur + brand-consistent overlay.
 *  - Escape key and backdrop click close it (unless dismissible={false}).
 *  - Traps Tab focus inside the panel and restores focus on close.
 *  - Locks body scroll while open. Entrance animation respects reduced-motion
 *    (neutralized by the global prefers-reduced-motion rule in index.css).
 */
const SIZES = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
};

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({
    isOpen,
    onClose,
    title,
    size = 'md',
    dismissible = true,
    showClose = true,
    className = '',
    children,
}) {
    const panelRef = useRef(null);
    const previouslyFocused = useRef(null);
    const titleId = useId();

    const handleClose = useCallback(() => {
        if (dismissible && onClose) onClose();
    }, [dismissible, onClose]);

    useEffect(() => {
        if (!isOpen) return undefined;

        previouslyFocused.current = document.activeElement;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleClose();
                return;
            }
            if (e.key !== 'Tab' || !panelRef.current) return;
            const focusables = panelRef.current.querySelectorAll(FOCUSABLE);
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // Move focus into the dialog once it has mounted.
        const focusTimer = setTimeout(() => {
            const target = panelRef.current?.querySelector(FOCUSABLE) || panelRef.current;
            target?.focus();
        }, 0);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
            clearTimeout(focusTimer);
            if (previouslyFocused.current instanceof HTMLElement) {
                previouslyFocused.current.focus();
            }
        };
    }, [isOpen, handleClose]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm modal-overlay-in"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) handleClose();
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                tabIndex={-1}
                className={`relative w-full ${SIZES[size] || SIZES.md} bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 p-6 sm:p-8 outline-none modal-panel-in ${className}`}
            >
                {showClose && dismissible && (
                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close dialog"
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    >
                        <X size={22} />
                    </button>
                )}
                {title && (
                    <h3 id={titleId} className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pr-8">{title}</h3>
                )}
                {children}
            </div>
        </div>,
        document.body
    );
}
